"use client";

import { c as _c } from "react/compiler-runtime";
import { MagnifyingGlass, SpinnerGap, X } from "@phosphor-icons/react";
import { useSearchParams } from "next/navigation";
import { useLocale } from "next-intl";
import { useEffect, useRef, useState, useTransition } from "react";
import { useListingTransition } from "@/components/storefront/ListingTransitionContext";
import { normalizeSearchQuery } from "@/lib/db/search-pattern";
const SEARCH_DEBOUNCE_MS = 300;

/**
 * Live search over the listing: edits `?q=` and lets the server re-render.
 * Writes through ListingTransitionContext so it builds on the same latest
 * intended params as the tiles and chips, but replaces inside its own
 * transition so the grid keeps showing the previous results instead of
 * swapping to a skeleton.
 */
export default function ListingSearchBox(t0) {
  const $ = _c(32);
  const {
    initialQuery
  } = t0;
  const isRTL = useLocale() === "ar";
  const {
    replaceWithParams,
    currentParams
  } = useListingTransition();
  const urlQuery = useSearchParams().get("q") ?? "";
  const [value, setValue] = useState(initialQuery);
  const [isPending, startTransition] = useTransition();
  const timer = useRef(null);
  let t1;
  if ($[0] !== urlQuery) {
    t1 = [urlQuery];
    $[0] = urlQuery;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const sentQueries = useRef(t1);
  let t2;
  let t3;
  if ($[2] !== urlQuery) {
    t2 = () => {
      const i = sentQueries.current.indexOf(urlQuery);
      if (i !== -1) {
        sentQueries.current = sentQueries.current.slice(i);
        return;
      }
      sentQueries.current = [urlQuery];
      setValue(urlQuery);
    };
    t3 = [urlQuery];
    $[2] = urlQuery;
    $[3] = t2;
    $[4] = t3;
  } else {
    t2 = $[3];
    t3 = $[4];
  }
  useEffect(t2, t3);
  let t4;
  if ($[5] !== currentParams || $[6] !== replaceWithParams) {
    t4 = function navigate(next) {
      const q = normalizeSearchQuery(next);
      if (q === (currentParams().get("q") ?? "")) {
        return;
      }
      sentQueries.current.push(q);
      replaceWithParams(params => {
        if (q) {
          params.set("q", q);
        } else {
          params.delete("q");
        }
        params.delete("page");
      }, startTransition);
    };
    $[5] = currentParams;
    $[6] = replaceWithParams;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const navigate = t4;
  let t5;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = function clearTimer() {
      if (timer.current) {
        clearTimeout(timer.current);
      }
      timer.current = null;
    };
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const clearTimer = t5;
  let t6;
  if ($[9] !== navigate || $[10] !== value) {
    t6 = () => {
      const q_0 = normalizeSearchQuery(value);
      if (q_0.length === 1) {
        return;
      }
      timer.current = setTimeout(() => navigate(value), SEARCH_DEBOUNCE_MS);
      return clearTimer;
    };
    $[9] = navigate;
    $[10] = value;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== value) {
    t7 = [value];
    $[12] = value;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  useEffect(t6, t7);
  let t8;
  if ($[14] !== navigate || $[15] !== value) {
    t8 = function handleSubmit(e) {
      e.preventDefault();
      clearTimer();
      navigate(value);
    };
    $[14] = navigate;
    $[15] = value;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  const handleSubmit = t8;
  const searchLabel = isRTL ? "\u0627\u0628\u062D\u062B \u0641\u064A \u0627\u0644\u0645\u0646\u062A\u062C\u0627\u062A..." : "Search products...";
  let t9;
  if ($[17] !== isPending || $[18] !== isRTL || $[19] !== value) {
    t9 = isPending ? <SpinnerGap className="absolute top-1/2 -translate-y-1/2 end-3 animate-spin text-muted-foreground" size={18} aria-hidden="true" /> : value ? <button type="button" onClick={() => setValue("")} aria-label={isRTL ? "\u0645\u0633\u062D \u0627\u0644\u0628\u062D\u062B" : "Clear search"} className="absolute top-1/2 -translate-y-1/2 end-3 text-muted-foreground hover:text-foreground"><X size={18} /></button> : null;
    $[17] = isPending;
    $[18] = isRTL;
    $[19] = value;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <MagnifyingGlass className="absolute top-1/2 -translate-y-1/2 start-3 text-muted-foreground" size={18} aria-hidden="true" />;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = e_0 => setValue(e_0.target.value);
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== searchLabel || $[24] !== value) {
    t12 = <input type="search" value={value} onChange={t11} placeholder={searchLabel} aria-label={searchLabel} className="w-full border border-border rounded-full py-2.5 ps-10 pe-10 text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30" />;
    $[23] = searchLabel;
    $[24] = value;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] !== t12 || $[27] !== t9) {
    t13 = <div className="relative">{t9}{t10}{t12}</div>;
    $[26] = t12;
    $[27] = t9;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  let t14;
  if ($[29] !== handleSubmit || $[30] !== t13) {
    t14 = <form onSubmit={handleSubmit} className="w-full">{t13}</form>;
    $[29] = handleSubmit;
    $[30] = t13;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  return t14;
}